import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useAuth } from "../../context/AuthContext";
import { can } from "../../utils/roles";
import PassengerForm, { PassengerData } from "../flights/PassengerForm";
import {
  getSavedPassengers,
  savePassengersToProfile,
} from "../../services/api";
import {
  FaUserPlus,
  FaTrash,
  FaPen,
  FaArrowLeft,
  FaTimes,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

/**
 * Saved passengers, moved out of the profile page into a module of its own.
 *
 * The list, the add/edit modal and the delete action all talk to the same
 * `/profiles/:uid/passengers` endpoints the profile page used to, so a passenger
 * saved here and one saved from the booking flow land in the same list.
 *
 * Access is governed by the `passengers` module — the same mechanism as every
 * other screen in the staff-facing product, granted through permission groups.
 */

const TYPE_LABEL: Record<PassengerData["passengerType"], string> = {
  ADT: "Adult",
  CHD: "Child",
  INF: "Infant",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const emptyPassenger = (): PassengerData => ({
  title: "",
  firstName: "",
  lastName: "",
  countryCode: "",
  phone: "",
  email: "",
  ffNumber: "",
  ffAirline: "",
  passportNumber: "",
  dob: "",
  expiryDate: "",
  issuingCountry: "",
  agreed: false,
  insured: false,
  passengerType: "ADT",
});

/** The page numbers to show, with first/last pinned and gaps marked by -1. */
function pageWindow(page: number, total: number): number[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set<number>([1, total]);
  for (let p = page - 1; p <= page + 1; p++) {
    if (p >= 1 && p <= total) set.add(p);
  }
  const sorted = Array.from(set).sort((a, b) => a - b);
  // Insert -1 wherever two consecutive numbers are more than one apart.
  const out: number[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push(-1);
    out.push(sorted[i]);
  }
  return out;
}

const Passengers = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { profile } = useAuth();
  const allowed = can(profile, "passengers");

  const [passengers, setPassengers] = useState<PassengerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Pagination state.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state. `editingIdx` is null when adding, the list index when editing.
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<PassengerData | null>(null);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!user || !allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setPassengers(await getSavedPassengers(user.uid));
      setError("");
    } catch (e: any) {
      setError("Could not load passengers.");
    } finally {
      setLoading(false);
    }
  }, [user, allowed]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(passengers.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = passengers.slice(start, start + pageSize);

  // A delete on the last page can leave `page` pointing past the end; reel it in.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const openAdd = () => {
    setEditingIdx(null);
    setDraft(emptyPassenger());
    setError("");
    setModalOpen(true);
  };

  const openEdit = (idx: number) => {
    setEditingIdx(idx);
    setDraft({ ...passengers[idx] });
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setDraft(null);
    setEditingIdx(null);
  };

  const handleSaveDraft = async () => {
    if (!user || !draft) return;

    if (!draft.firstName.trim() || !draft.lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    if (draft.passengerType !== "INF" && !draft.passportNumber.trim()) {
      setError("Passport number is required.");
      return;
    }
    const duplicate = passengers.some(
      (p, i) =>
        i !== editingIdx &&
        p.passportNumber &&
        draft.passportNumber &&
        p.passportNumber === draft.passportNumber,
    );
    if (duplicate) {
      setError("A passenger with this passport number already exists.");
      return;
    }

    const updated =
      editingIdx === null
        ? [...passengers, draft]
        : passengers.map((p, i) => (i === editingIdx ? draft : p));

    setSaving(true);
    try {
      await savePassengersToProfile(user.uid, updated);
      setPassengers(updated);
      closeModal();
    } catch (e: any) {
      setError(
        "Failed to save passenger: " +
          (e?.response?.data?.error || e?.message || "unknown error"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (idx: number) => {
    if (!user) return;
    const updated = passengers.filter((_, i) => i !== idx);
    try {
      await savePassengersToProfile(user.uid, updated);
      setPassengers(updated);
    } catch (e: any) {
      setError(
        "Failed to remove passenger: " +
          (e?.response?.data?.error || e?.message || "unknown error"),
      );
    }
  };

  if (!user) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <FaUsers className="mx-auto text-gray-300 mb-4" size={40} />
        <h1 className="text-2xl font-sora font-bold text-heading mb-2">
          Saved Passengers
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Sign in to see and manage your saved passengers.
        </p>
        <button
          className="bg-primary text-white font-semibold px-5 py-2 rounded-full hover:bg-blue-800 transition"
          onClick={() => navigate("/login")}
        >
          Login / Sign Up
        </button>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <FaUsers className="mx-auto text-gray-300 mb-3" size={30} />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            Not available
          </h1>
          <p className="text-sm text-gray-500">
            You do not have permission to manage passengers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <button
        className="text-sm font-semibold text-primary hover:text-heading transition flex items-center mb-4"
        onClick={() => navigate(-1)}
        type="button"
      >
        <FaArrowLeft className="w-4 h-4 mr-2" />
        Back
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-sora font-bold text-heading">
            Saved Passengers
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            The people you usually travel with, ready to use when booking.
          </p>
        </div>
        <button
          className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded hover:bg-gray-100 hover:text-primary transition flex items-center justify-center min-w-[140px]"
          onClick={openAdd}
          type="button"
        >
          <FaUserPlus className="w-4 h-4 mr-2" />
          Add Passenger
        </button>
      </div>

      {error && !modalOpen && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : passengers.length === 0 ? (
        <div className="py-16 text-center text-gray-500 text-sm">
          No saved passengers yet. Add one to make your next booking faster.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Passenger</th>
                    <th className="text-left px-4 py-2.5 font-medium">Passport</th>
                    <th className="text-left px-4 py-2.5 font-medium">Date of birth</th>
                    <th className="text-left px-4 py-2.5 font-medium">Expiry</th>
                    <th className="text-left px-4 py-2.5 font-medium">Contact</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageItems.map((passenger, idx) => {
                    const realIdx = start + idx;
                    return (
                      <tr key={passenger.passportNumber + realIdx}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">
                            {passenger.title} {passenger.firstName} {passenger.lastName}
                          </div>
                          <span className="inline-block mt-0.5 rounded-full bg-blue-50 text-primary text-xs font-medium px-2 py-0.5">
                            {TYPE_LABEL[passenger.passengerType]}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">
                          {passenger.passportNumber || "—"}
                          {passenger.issuingCountry ? (
                            <div className="text-xs text-gray-400">
                              {passenger.issuingCountry}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">
                          {passenger.dob || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">
                          {passenger.expiryDate || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">
                          <div>{passenger.email || "—"}</div>
                          <div className="text-xs text-gray-400">
                            {passenger.countryCode} {passenger.phone}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button
                            className="text-blue-500 hover:text-primary"
                            onClick={() => openEdit(realIdx)}
                            title="Edit Passenger"
                            type="button"
                          >
                            <FaPen className="w-4 h-4" />
                          </button>
                          <button
                            className="text-blue-500 hover:text-primary ml-3"
                            onClick={() => handleRemove(realIdx)}
                            title="Remove Passenger"
                            type="button"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Pagination ── */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-500">
              Showing {passengers.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, passengers.length)} of{" "}
              {passengers.length} passengers
            </div>

            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500">
                Per page
                <select
                  className="ml-2 border border-gray-300 rounded px-2 py-1 text-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex items-center gap-1">
                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  aria-label="Previous page"
                  type="button"
                >
                  <FaChevronLeft className="w-3 h-3" />
                </button>

                {pageWindow(safePage, totalPages).map((p, i) =>
                  p === -1 ? (
                    <span key={`gap-${i}`} className="px-1 text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded text-sm font-medium ${
                        p === safePage
                          ? "bg-primary text-white"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      onClick={() => setPage(p)}
                      type="button"
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  className="inline-flex items-center justify-center w-8 h-8 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  aria-label="Next page"
                  type="button"
                >
                  <FaChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Add / edit modal ── */}
      {modalOpen && draft && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-sora font-bold text-heading">
                {editingIdx === null ? "Add Passenger" : "Edit Passenger"}
              </h2>
              <button
                onClick={closeModal}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-700"
                type="button"
              >
                <FaTimes />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-4 flex-1">
              {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Passenger Type
                </label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={draft.passengerType}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      passengerType: e.target.value as
                        | "ADT"
                        | "CHD"
                        | "INF",
                    })
                  }
                >
                  <option value="ADT">Adult (ADT)</option>
                  <option value="CHD">Child (CHD)</option>
                  <option value="INF">Infant (INF)</option>
                </select>
              </div>

              <PassengerForm
                index={1}
                type={draft.passengerType}
                value={draft}
                onChange={(data) => setDraft(data)}
              />
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
              <button
                className="bg-white text-gray-700 text-sm font-semibold px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition"
                onClick={closeModal}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded hover:bg-gray-100 hover:text-primary transition flex items-center justify-center min-w-[110px]"
                onClick={handleSaveDraft}
                disabled={saving}
                type="button"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Passengers;
